/* ⚠️ GENERATED from app/services/aiPending.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/aiPending.ts — "Allo AI is thinking", as a fact rather than as a
// feeling one device happens to be having.
//
// 🟥 THE INDICATOR WAS LOCAL STATE ON THE DEVICE THAT ASKED. A row pushed into
//    one client's message array, plus a DeviceEventEmitter event that never
//    leaves the process. So asking on the phone and then opening the web tab
//    showed nothing at all until the answer arrived, and the person on the
//    other end of the conversation watched a question sit there unanswered with
//    no sign anything was happening.
//
// 🟢 SO IT IS ONE FIELD ON THE CHAT DOCUMENT, and every client already has a
//    listener on that document. Nothing new is subscribed to, nothing is
//    polled, and both clients agree because there is only one thing to agree
//    with. The phone and the browser show the same state for the same reason.
//
// 🟥 AND IT EXPIRES, BECAUSE THE CLIENT THAT SET IT CAN DIE. The request runs
//    on whichever client asked; close that tab, lose that network, kill that
//    app, and nothing will ever clear the marker. A field that can get stuck on
//    is worse than no field: the chat would show a thinking indicator for ever.
//    So a marker is only believed while it is fresh, and the age is decided
//    here rather than by whoever happens to read it.

/** How long a request is allowed to take before we stop believing in it. */
export const AI_PENDING_TTL_MS = 90000;
/** Milliseconds out of whichever shape the timestamp arrived in. */
export function pendingAt(p) {
  const v = p?.at;
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') {
    try {
      return v.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v._seconds === 'number') return v._seconds * 1000;
  return 0;
}

/**
 * Is Allo AI working on something in this chat right now?
 *
 * 🟥 A MARKER WITH NO TIME ON IT IS NOT BELIEVED AT ALL. `at` is written with a
 *    server timestamp, which is null on the writing client for the moment
 *    between the local write and the server's echo — and reading that as "0"
 *    would make the asker's own indicator flicker off and on again. So an
 *    unresolved timestamp counts as just-now, and only a marker with a real
 *    time that has passed is discarded.
 *
 * @param now injected so the rule can be tested without waiting ninety seconds
 */
export function aiPendingActive(doc, now = Date.now()) {
  const p = doc?.aiPending;
  if (!p || typeof p !== 'object') return false;
  const at = pendingAt(p);
  if (!at) return true; // written, not yet stamped
  return now - at < AI_PENDING_TTL_MS;
}

/**
 * A marker nobody is going to clear, because whoever set it is gone.
 *
 * 🟢 THE ASKER'S OWN CLIENT CLEANS UP AFTER ITSELF. Anybody can see a stale
 *    marker, but only the account that wrote one has any business deleting it —
 *    a stranger clearing your chat's fields is a write nobody asked for, and
 *    the rules would be right to refuse it.
 */
export function aiPendingStale(doc, now = Date.now()) {
  const p = doc?.aiPending;
  if (!p || typeof p !== 'object') return false;
  const at = pendingAt(p);
  return !!at && now - at >= AI_PENDING_TTL_MS;
}

/** True when this marker was written by this very device. */
export const aiPendingIsMine = (doc, uid) => !!doc?.aiPending && !!uid && doc.aiPending.by === uid;

/**
 * What to write when a request starts.
 *
 * 🟢 THE VALUE IS BUILT HERE, NOT AT THE TWO CALL SITES, so the phone and the
 *    browser cannot write two different shapes into the same field and then
 *    disagree about how to read it. The caller supplies the server timestamp,
 *    because only the caller has its own database library.
 */
export function aiPendingWrite(uid, kind, at, device) {
  return {
    by: uid,
    device: device || null,
    kind,
    at
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 A MARKER THAT SURVIVED ITS OWN ANSWER.

   The asker's client deletes this field when the request finishes. That write
   can simply not happen: the app was killed, the network went, the rules
   refused it, or the reply landed on a client that never wrote the marker at
   all. And nothing else clears it, so the field sits there for its full ninety
   seconds — which is why walking back into a chat whose LAST message is an AI
   answer showed a thinking indicator for a moment before it aged out.

   🟥 A TIMESTAMP ALONE CANNOT SETTLE IT. Ninety seconds is a guess about how
      long a request takes, and the whole point of the report is that the
      request was already over.

   🟢 THE EXACT SIGNAL IS THAT AN ANSWER EXISTS AND IS NEWER. What ends this
      marker is an assistant message; if the chat already holds one written
      after the marker was, the request it describes is finished — whatever
      happened to the write that was supposed to say so. It needs no clock
      threshold, it repairs every marker already stuck in the field with no
      migration, and it is a fact the client is holding anyway.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * 🟢 A SMALL SLACK, AND SMALL ON PURPOSE. `at` is a server timestamp and a
 *    reply's time may be the writing device's, so the two can disagree by a
 *    fraction of a second. Anything generous enough to absorb a real clock
 *    problem would also absorb a SECOND question asked shortly after the first
 *    answer — and suppressing the indicator on that is the same bug facing the
 *    other way.
 */
export const AI_ANSWER_SKEW_MS = 2000;

/** Has an assistant message already landed for this marker? */
export function aiPendingAnswered(doc, lastAiAt) {
  const at = pendingAt(doc?.aiPending);
  const ai = Number(lastAiAt) || 0;
  if (!ai || !at) return false;
  /* 🟥 `!at` IS "WRITTEN, NOT YET STAMPED" — a marker from a moment ago. An
     answer that predates it cannot be its answer, so this must not be true. */
  return ai >= at - AI_ANSWER_SKEW_MS;
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 AND IT APPEARED AT THE INSTANT OF SEND.

   The indicator went up in the same frame as the message, which reads as the
   assistant answering before it can have heard the question — and on a fast
   reply it appears and disappears inside a few hundred milliseconds, which is
   a flash rather than information.

   🟢 SO IT WAITS. Nothing is said for the first moment of a request, and if the
      answer arrives inside it nothing is ever said at all. That is the correct
      amount of feedback for a request that was over before you could read a
      status about it.

   🟥 MEASURED FROM WHEN THIS CLIENT FIRST SAW THE MARKER, NOT FROM `at`. On the
      asking device `at` is an unresolved server timestamp for the first moment
      of its life — the very moment the grace is about — so timing from it would
      make the delay depend on how fast Firestore echoed the write back.
   ═══════════════════════════════════════════════════════════════════════════ */

/** How long a request runs before it is worth saying anything about it. */
export const AI_PENDING_GRACE_MS = 800;
/** Should a thinking indicator be on screen right now? */
export function aiPendingShow(doc, v = {}) {
  const now = Number(v.now) || Date.now();
  if (!aiPendingActive(doc, now)) return false;
  if (aiPendingAnswered(doc, v.lastAiAt)) return false;
  const since = Number(v.since) || 0;
  /* 🟢 NOT SEEN YET IS NOT "SEEN LONG AGO". A marker this client has not begun
     timing has not served its grace, and treating a missing `since` as 0 would
     make `now - 0` enormous and show it immediately — the exact behaviour the
     grace exists to remove. */
  if (!since) return false;
  return now - since >= AI_PENDING_GRACE_MS;
}
